// Admin page for the generalized agent-eval harness.
//
// Views, switched via tabs:
//   1. Triage queue — unlabeled flagged results (the work in front of you)
//   2. Runs — list of all runs, click into a run to see results
//   3. Run detail / Result detail — full transcript + judge justifications +
//      inline triage labeling
//   4. Feedback lessons — the agent learning loop's ledger (what each human
//      judgment turned into), with the stats strip + run-now trigger
//   5. Eval cases — curation (accept / reject) of the cases the reflecting
//      agent proposed (kanban 651f3aa5)
//
// Backend: app/routes/agent_eval.py, app/routes/feedback_learning.py.
// Service: frontend/src/services/agentEval.ts, services/feedbackLearning.ts.

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  agentEvalApi,
  type ResultDetail,
  type ResultSummary,
  type RunDetail,
  type RunSummary,
  type TriageLabel,
  TRIAGE_LABEL_DISPLAY,
} from '../services/agentEval';
import styles from './AgentEvalPage.module.css';
import { FeedbackCasesTab } from './agentEval/FeedbackCasesTab';
import { FeedbackLessonsTab } from './agentEval/FeedbackLessonsTab';
import { LearningLoopStats } from './agentEval/LearningLoopStats';
import { formatTs } from './agentEval/format';

type Tab = 'triage' | 'runs' | 'lessons' | 'cases';
const LOOP_TABS: readonly Tab[] = ['lessons', 'cases'];

const TRIAGE_OPTIONS: TriageLabel[] = [
  'real_bug',
  'judge_wrong',
  'sim_drifted_agent_ok',
  'other',
];

function scoreClass(s: number | null): string {
  if (s === null) return styles.score;
  return `${styles.score} ${s >= 2.5 ? styles.ok : styles.bad}`;
}

function ResultRow({
  r,
  onClick,
}: {
  r: ResultSummary;
  onClick: () => void;
}): React.ReactElement {
  const label = r.triage_label;
  return (
    <tr className={styles.clickable} onClick={onClick}>
      <td>{r.case_id}</td>
      <td>
        {r.error ? (
          <span className={`${styles.tag} ${styles.errored}`}>errored</span>
        ) : r.flagged ? (
          <span className={`${styles.tag} ${styles.flagged}`}>flagged</span>
        ) : (
          <span className={`${styles.tag} ${styles.ok}`}>ok</span>
        )}
      </td>
      <td>
        <span className={scoreClass(r.avg_score)}>
          {r.avg_score === null ? '—' : r.avg_score.toFixed(2)}
        </span>
      </td>
      <td>{r.judge_verdict ?? '—'}</td>
      <td>{r.conversation_turns}</td>
      <td>
        {label ? (
          <span className={`${styles.tag} ${styles['label_' + label]}`}>
            {TRIAGE_LABEL_DISPLAY[label]}
          </span>
        ) : r.flagged ? (
          <span className={`${styles.tag} ${styles.pending}`}>untriaged</span>
        ) : (
          <span className={styles.score}>—</span>
        )}
      </td>
    </tr>
  );
}

function ResultDetailView({
  detail,
  onLabelChange,
  onBack,
}: {
  detail: ResultDetail;
  onLabelChange: (next: ResultDetail) => void;
  onBack: () => void;
}): React.ReactElement {
  const [notes, setNotes] = useState<string>(detail.triage_notes ?? '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotes(detail.triage_notes ?? '');
  }, [detail.id, detail.triage_notes]);

  const apply = useCallback(
    async (label: TriageLabel) => {
      setSaving(true);
      setError(null);
      try {
        const next = await agentEvalApi.applyTriageLabel(
          detail.id,
          label,
          notes || undefined,
        );
        onLabelChange(next);
      } catch (e) {
        setError((e as Error).message || 'failed to apply label');
      } finally {
        setSaving(false);
      }
    },
    [detail.id, notes, onLabelChange],
  );

  const clear = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const next = await agentEvalApi.clearTriageLabel(detail.id);
      onLabelChange(next);
    } catch (e) {
      setError((e as Error).message || 'failed to clear label');
    } finally {
      setSaving(false);
    }
  }, [detail.id, onLabelChange]);

  return (
    <div className={styles.detail}>
      <button className={styles['back-link']} onClick={onBack}>
        ← back
      </button>
      <h2>{detail.case_id}</h2>
      <div className={styles.meta}>
        <span>
          verdict: <b>{detail.judge_verdict ?? '—'}</b>
        </span>
        <span>
          avg score:{' '}
          <b className={scoreClass(detail.avg_score)}>
            {detail.avg_score === null ? '—' : detail.avg_score.toFixed(2)}
          </b>
        </span>
        <span>
          turns: <b>{detail.conversation_turns}</b>
        </span>
        <span>
          latency: <b>{detail.latency_ms} ms</b>
        </span>
        {detail.error && (
          <span>
            error: <b>{detail.error}</b>
          </span>
        )}
      </div>

      <div className={styles['triage-card']}>
        <h3>Triage label</h3>
        <div>
          {detail.triage_label
            ? `Currently labeled: ${TRIAGE_LABEL_DISPLAY[detail.triage_label]}`
            : 'No label applied yet.'}
        </div>
        <div className={styles.actions}>
          {TRIAGE_OPTIONS.map((opt) => (
            <button
              key={opt}
              disabled={saving}
              className={detail.triage_label === opt ? styles.active : ''}
              onClick={() => apply(opt)}
            >
              {TRIAGE_LABEL_DISPLAY[opt]}
            </button>
          ))}
          {detail.triage_label && (
            <button className={styles.clear} disabled={saving} onClick={clear}>
              Clear
            </button>
          )}
        </div>
        <div className={styles['notes-row']}>
          <textarea
            placeholder="Notes (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
          />
        </div>
        {error && <div className={styles.error}>{error}</div>}
      </div>

      {detail.judge_justifications?.items && (
        <>
          <h3>Rubric scores</h3>
          <div className={styles['rubric-grid']}>
            {detail.judge_justifications.items.map((item) => (
              <React.Fragment key={item.name}>
                <div className={styles.name}>
                  {item.name}
                  <span className={scoreClass(item.score)}>{item.score}/3</span>
                </div>
                <div>{item.justification}</div>
              </React.Fragment>
            ))}
          </div>
          {detail.judge_justifications.summary && (
            <p style={{ marginTop: 12, fontSize: 13, color: '#4a5568' }}>
              <b>Summary:</b> {detail.judge_justifications.summary}
            </p>
          )}
        </>
      )}

      <h3>Transcript</h3>
      <div className={styles.transcript}>
        {detail.transcript.map((turn, i) => (
          <div key={i} className={`${styles.turn} ${styles[turn.role] ?? ''}`}>
            <div className={styles.role}>{turn.role}</div>
            {turn.role === 'tool' ? (
              <>
                <b>{turn.tool_name}</b>(
                {turn.tool_args ? JSON.stringify(turn.tool_args).slice(0, 300) : ''}) →{' '}
                {turn.tool_result !== null && turn.tool_result !== undefined
                  ? JSON.stringify(turn.tool_result).slice(0, 300)
                  : '(no result)'}
              </>
            ) : (
              turn.content
            )}
          </div>
        ))}
        {detail.transcript.length === 0 && (
          <div className={styles.empty}>(empty transcript)</div>
        )}
      </div>

      {detail.final_db_state && (
        <>
          <h3>Final DB state</h3>
          <pre
            style={{
              background: '#f7f8fa',
              border: '1px solid #e2e8f0',
              padding: 12,
              borderRadius: 8,
              fontSize: 12,
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(detail.final_db_state, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
}

export default function AgentEvalPage(): React.ReactElement {
  const [tab, setTab] = useState<Tab>('triage');
  const [flagged, setFlagged] = useState<ResultSummary[]>([]);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [selectedRun, setSelectedRun] = useState<RunDetail | null>(null);
  const [selectedResult, setSelectedResult] = useState<ResultDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [triageFilter, setTriageFilter] =
    useState<'unlabeled' | 'labeled' | 'any'>('unlabeled');
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>('');
  // Bumped after a curation so the learning-loop stats strip reloads.
  const [statsRefresh, setStatsRefresh] = useState<number>(0);

  const loadFlagged = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await agentEvalApi.listFlagged({
        triage: triageFilter,
        agent_type: agentTypeFilter || undefined,
        limit: 200,
      });
      setFlagged(data);
    } catch (e) {
      setError((e as Error).message || 'failed to load flagged results');
    } finally {
      setLoading(false);
    }
  }, [triageFilter, agentTypeFilter]);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await agentEvalApi.listRuns({
        agent_type: agentTypeFilter || undefined,
        limit: 100,
      });
      setRuns(data);
    } catch (e) {
      setError((e as Error).message || 'failed to load runs');
    } finally {
      setLoading(false);
    }
  }, [agentTypeFilter]);

  useEffect(() => {
    if (selectedResult) return;
    if (selectedRun) return;
    if (tab === 'triage') void loadFlagged();
    else if (tab === 'runs') void loadRuns();
    // 'lessons' / 'cases' load their own data.
  }, [tab, loadFlagged, loadRuns, selectedResult, selectedRun]);

  const openResult = useCallback(async (resultId: string) => {
    setLoading(true);
    setError(null);
    try {
      const detail = await agentEvalApi.getResult(resultId);
      setSelectedResult(detail);
    } catch (e) {
      setError((e as Error).message || 'failed to load result');
    } finally {
      setLoading(false);
    }
  }, []);

  const openRun = useCallback(async (runId: string) => {
    setLoading(true);
    setError(null);
    try {
      const detail = await agentEvalApi.getRun(runId);
      setSelectedRun(detail);
    } catch (e) {
      setError((e as Error).message || 'failed to load run');
    } finally {
      setLoading(false);
    }
  }, []);

  const flaggedCount = useMemo(
    () => flagged.filter((r) => !r.triage_label).length,
    [flagged],
  );

  if (selectedResult) {
    return (
      <div className={styles.page}>
        <ResultDetailView
          detail={selectedResult}
          onLabelChange={(next) => {
            setSelectedResult(next);
            // refresh background lists so labels propagate
            void loadFlagged();
          }}
          onBack={() => setSelectedResult(null)}
        />
      </div>
    );
  }

  if (selectedRun) {
    return (
      <div className={styles.page}>
        <div className={styles.detail}>
          <button className={styles['back-link']} onClick={() => setSelectedRun(null)}>
            ← back to runs
          </button>
          <h2>
            {selectedRun.agent_type} · {selectedRun.rubric_name}
          </h2>
          <div className={styles.meta}>
            <span>
              status: <b>{selectedRun.status}</b>
            </span>
            <span>
              started: <b>{formatTs(selectedRun.started_at)}</b>
            </span>
            <span>
              completed: <b>{formatTs(selectedRun.completed_at)}</b>
            </span>
            <span>
              total: <b>{selectedRun.summary_total}</b>
            </span>
            <span>
              flagged: <b>{selectedRun.summary_flagged}</b>
            </span>
            <span>
              avg score:{' '}
              <b className={scoreClass(selectedRun.summary_avg_score)}>
                {selectedRun.summary_avg_score === null
                  ? '—'
                  : selectedRun.summary_avg_score.toFixed(2)}
              </b>
            </span>
          </div>
          <table className={styles.list}>
            <thead>
              <tr>
                <th>Case</th>
                <th>Status</th>
                <th>Avg</th>
                <th>Verdict</th>
                <th>Turns</th>
                <th>Triage</th>
              </tr>
            </thead>
            <tbody>
              {selectedRun.results.map((r) => (
                <ResultRow
                  key={r.id}
                  r={r}
                  onClick={() => void openResult(r.id)}
                />
              ))}
              {selectedRun.results.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.empty}>
                    no results recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Agent Eval</h1>
          <div className={styles.sub}>
            Triage flagged transcripts and audit harness runs.
          </div>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'triage' ? styles.active : ''}`}
          onClick={() => setTab('triage')}
        >
          Triage queue
          {flaggedCount > 0 && tab === 'triage' && (
            <span className={styles.count}>{flaggedCount}</span>
          )}
        </button>
        <button
          className={`${styles.tab} ${tab === 'runs' ? styles.active : ''}`}
          onClick={() => setTab('runs')}
        >
          Runs
        </button>
        <button
          className={`${styles.tab} ${tab === 'lessons' ? styles.active : ''}`}
          onClick={() => setTab('lessons')}
          data-action="agent_eval_tab_open"
          data-record-value="tab"
          value="lessons"
          data-outcome="open"
        >
          Feedback lessons
        </button>
        <button
          className={`${styles.tab} ${tab === 'cases' ? styles.active : ''}`}
          onClick={() => setTab('cases')}
          data-action="agent_eval_tab_open"
          data-record-value="tab"
          value="cases"
          data-outcome="open"
        >
          Eval cases
        </button>
      </div>

      {LOOP_TABS.includes(tab) && <LearningLoopStats refreshKey={statsRefresh} />}
      {tab === 'lessons' && <FeedbackLessonsTab />}
      {tab === 'cases' && (
        <FeedbackCasesTab onCurated={() => setStatsRefresh((n) => n + 1)} />
      )}

      {LOOP_TABS.includes(tab) ? null : (
      <>
      <div className={styles.filters}>
        <label>Agent</label>
        <input
          placeholder="(any)"
          value={agentTypeFilter}
          onChange={(e) => setAgentTypeFilter(e.target.value)}
        />
        {tab === 'triage' && (
          <>
            <label>Triage state</label>
            <select
              value={triageFilter}
              onChange={(e) =>
                setTriageFilter(e.target.value as 'unlabeled' | 'labeled' | 'any')
              }
            >
              <option value="unlabeled">Unlabeled</option>
              <option value="labeled">Labeled</option>
              <option value="any">Any</option>
            </select>
          </>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {tab === 'triage' ? (
        <table className={styles.list}>
          <thead>
            <tr>
              <th>Case</th>
              <th>Status</th>
              <th>Avg</th>
              <th>Verdict</th>
              <th>Turns</th>
              <th>Triage</th>
            </tr>
          </thead>
          <tbody>
            {flagged.map((r) => (
              <ResultRow key={r.id} r={r} onClick={() => void openResult(r.id)} />
            ))}
            {!loading && flagged.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  {triageFilter === 'unlabeled'
                    ? 'queue is clear — nothing flagged and untriaged'
                    : 'no flagged results match these filters'}
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <table className={styles.list}>
          <thead>
            <tr>
              <th>Agent</th>
              <th>Rubric</th>
              <th>Status</th>
              <th>Started</th>
              <th>Total</th>
              <th>Flagged</th>
              <th>Avg</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr
                key={run.id}
                className={styles.clickable}
                onClick={() => void openRun(run.id)}
              >
                <td>{run.agent_type}</td>
                <td>{run.rubric_name}</td>
                <td>{run.status}</td>
                <td>{formatTs(run.started_at)}</td>
                <td>{run.summary_total}</td>
                <td>{run.summary_flagged}</td>
                <td>
                  <span className={scoreClass(run.summary_avg_score)}>
                    {run.summary_avg_score === null
                      ? '—'
                      : run.summary_avg_score.toFixed(2)}
                  </span>
                </td>
              </tr>
            ))}
            {!loading && runs.length === 0 && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  no runs recorded yet
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      </>
      )}
    </div>
  );
}
